import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, ShoppingCart, TrendingUp, CheckCircle, Clock,
  AlertTriangle, DollarSign, Activity, ChevronRight,
  ArrowUpRight, ArrowDownRight, Crown, CreditCard,
  Settings, Building2, FileText, BarChart3, Zap,
  RefreshCw, Calendar, Eye, UserPlus, Shield,
  Sparkles, CircleDot, Database, WifiOff
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import API from '../../api';

// ─── Helpers ─────────────────────────────────────────────────

function getMonthName(monthIndex) {
  return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][monthIndex];
}

function getLastNMonths(n = 6) {
  const months = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      month: d.getMonth(),
      year: d.getFullYear(),
      label: getMonthName(d.getMonth()),
      fullLabel: `${getMonthName(d.getMonth())} ${d.getFullYear()}`
    });
  }
  return months;
}

function calcTrend(current, previous) {
  if (!previous || previous === 0) {
    return current > 0 ? { value: '+100%', up: true } : { value: '0%', up: true };
  }
  const change = ((current - previous) / previous) * 100;
  return {
    value: `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`,
    up: change >= 0
  };
}

function buildMonthlyDataFromUsers(users, months) {
  return months.map(m => {
    const count = users.filter(u => {
      const d = new Date(u.createdAt);
      return d.getMonth() === m.month && d.getFullYear() === m.year;
    }).length;
    return { ...m, users: count };
  });
}

function buildMonthlyDataFromPayments(payments, months) {
  return months.map(m => {
    const monthPayments = (payments || []).filter(p => {
      const d = new Date(p.createdAt || p.paymentDate);
      return d.getMonth() === m.month && d.getFullYear() === m.year;
    });
    const revenue = monthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    return { ...m, revenue, orders: monthPayments.length };
  });
}

function mergeMonthlyData(userData, paymentData) {
  return userData.map((item, i) => ({
    label: item.label,
    fullLabel: item.fullLabel,
    users: item.users,
    revenue: paymentData[i]?.revenue || 0,
    orders: paymentData[i]?.orders || 0
  }));
}

// ─── Custom Tooltip ─────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '12px',
      padding: '14px 18px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
    }}>
      <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 8px 0', fontWeight: 600 }}>
        {label}
      </p>
      {payload.map((entry, i) => (
        <p key={i} style={{
          color: entry.color, fontSize: '14px', margin: '4px 0',
          fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: entry.color, display: 'inline-block'
          }} />
          {entry.name}:{' '}
          {entry.name?.toLowerCase().includes('revenue')
            ? `Rs${entry.value.toLocaleString()}`
            : entry.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

// ─── Animated Counter ────────────────────────────────────────
function AnimatedNumber({ value, prefix = '', suffix = '' }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const num = typeof value === 'number' ? value : parseInt(value) || 0;
    if (num === 0) { setDisplay(0); return; }
    const duration = 1200;
    const steps = 40;
    const increment = num / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= num) {
        setDisplay(num);
        clearInterval(timer);
      } else {
        setDisplay(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);
  return <>{prefix}{display.toLocaleString()}{suffix}</>;
}

// ─── Stat Card ───────────────────────────────────────────────
function StatCard({ title, value, icon, gradient, trend, trendUp, subtitle, delay = 0, onClick }) {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#ffffff',
        borderRadius: '16px',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: visible
          ? hovered ? 'translateY(-6px) scale(1.02)' : 'translateY(0)'
          : 'translateY(30px)',
        opacity: visible ? 1 : 0,
        boxShadow: hovered
          ? '0 20px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)'
          : '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: hovered ? '4px' : '3px',
        background: gradient, transition: 'height 0.3s'
      }} />

      <div style={{
        position: 'absolute', right: -10, bottom: -10,
        opacity: 0.04,
        transform: `scale(${hovered ? 4.5 : 4})`,
        transition: 'transform 0.5s', color: '#000'
      }}>{icon}</div>

      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: '16px', position: 'relative'
      }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '14px',
          background: gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff',
          transition: 'transform 0.3s',
          transform: hovered ? 'rotate(-8deg) scale(1.1)' : 'rotate(0)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
        }}>{icon}</div>
        {trend && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '4px 10px', borderRadius: '20px',
            background: trendUp ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            color: trendUp ? '#059669' : '#dc2626',
            fontSize: '12px', fontWeight: 700
          }}>
            {trendUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {trend}
          </div>
        )}
      </div>

      <div style={{
        fontSize: '13px', fontWeight: 600, color: '#64748b',
        marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'
      }}>{title}</div>

      <div style={{
        fontSize: '30px', fontWeight: 800, color: '#0f172a',
        lineHeight: 1.2, marginBottom: '4px'
      }}>
        {typeof value === 'number'
          ? <AnimatedNumber value={value} />
          : value}
      </div>

      {subtitle && (
        <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ─── Quick Action Button ─────────────────────────────────────
function QuickActionButton({ icon, label, description, gradient, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '14px',
        padding: '14px 18px',
        background: hovered
          ? 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))'
          : '#f8fafc',
        border: '1px solid',
        borderColor: hovered ? 'rgba(99,102,241,0.2)' : '#f1f5f9',
        borderRadius: '14px', cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        width: '100%', textAlign: 'left',
        transform: hovered ? 'translateX(6px)' : 'translateX(0)',
        boxShadow: hovered ? '0 4px 12px rgba(99,102,241,0.1)' : 'none',
      }}
    >
      <div style={{
        width: '42px', height: '42px', borderRadius: '12px',
        background: gradient, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        color: '#fff', flexShrink: 0,
        transition: 'transform 0.3s',
        transform: hovered ? 'scale(1.1) rotate(-5deg)' : 'scale(1)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{label}</div>
        {description && (
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{description}</div>
        )}
      </div>
      <ChevronRight size={18} style={{
        color: '#94a3b8', transition: 'all 0.3s',
        transform: hovered ? 'translateX(4px)' : 'translateX(0)',
        opacity: hovered ? 1 : 0.5
      }} />
    </button>
  );
}

// ─── Section Header ──────────────────────────────────────────
function SectionHeader({ icon, title, subtitle, action }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'center', marginBottom: '20px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '10px',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff'
        }}>{icon}</div>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>{title}</h3>
          {subtitle && <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────
function EmptyState({ icon, message }) {
  return (
    <div style={{
      textAlign: 'center', padding: '40px 20px', color: '#94a3b8'
    }}>
      <div style={{ opacity: 0.3, marginBottom: 12 }}>{icon}</div>
      <p style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{message}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════
export default function AdminDashboard() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Raw data from APIs
  const [allUsers, setAllUsers] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState(null);

  // ─── Fetch All Data ─────────────────────────────
  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Parallel API calls
      const results = await Promise.allSettled([
        API.get('/users/admin/users', { headers }),
        API.get('/subscriptions/stats', { headers }),
        API.get('/payments', { headers }),          // try fetching payments
      ]);

      // 1. Users
      if (results[0].status === 'fulfilled') {
        setAllUsers(results[0].value.data || []);
      }

      // 2. Subscription stats
      if (results[1].status === 'fulfilled') {
        setSubscriptionStats(results[1].value.data?.stats || results[1].value.data || null);
      }

      // 3. Payments
      if (results[2].status === 'fulfilled') {
        const payData = results[2].value.data;
        setAllPayments(
          Array.isArray(payData) ? payData
          : Array.isArray(payData?.payments) ? payData.payments
          : []
        );
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data');
    }

    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  // ─── Derived Stats (all dynamic) ────────────────
  const computed = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    // Previous month
    const prevDate = new Date(thisYear, thisMonth - 1, 1);
    const prevMonth = prevDate.getMonth();
    const prevYear = prevDate.getFullYear();

    // User counts
    const totalUsers = allUsers.length;
    const activeUsers = allUsers.filter(u => u.status === 'active').length;
    const trialUsers = allUsers.filter(u => u.status === 'trial').length;
    const expiredUsers = allUsers.filter(u =>
      u.status === 'expired' || u.status === 'blocked'
    ).length;

    // New users this month vs last month
    const newUsersThisMonth = allUsers.filter(u => {
      const d = new Date(u.createdAt);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    }).length;

    const newUsersLastMonth = allUsers.filter(u => {
      const d = new Date(u.createdAt);
      return d.getMonth() === prevMonth && d.getFullYear() === prevYear;
    }).length;

    // Payment / Revenue
    const totalRevenue = allPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const totalOrders = allPayments.length;

    const revenueThisMonth = allPayments.filter(p => {
      const d = new Date(p.createdAt || p.paymentDate);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    }).reduce((sum, p) => sum + (p.amount || 0), 0);

    const revenueLastMonth = allPayments.filter(p => {
      const d = new Date(p.createdAt || p.paymentDate);
      return d.getMonth() === prevMonth && d.getFullYear() === prevYear;
    }).reduce((sum, p) => sum + (p.amount || 0), 0);

    const ordersThisMonth = allPayments.filter(p => {
      const d = new Date(p.createdAt || p.paymentDate);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    }).length;

    const ordersLastMonth = allPayments.filter(p => {
      const d = new Date(p.createdAt || p.paymentDate);
      return d.getMonth() === prevMonth && d.getFullYear() === prevYear;
    }).length;

    // Trends
    const userTrend = calcTrend(newUsersThisMonth, newUsersLastMonth);
    const activeRate = totalUsers > 0
      ? ((activeUsers / totalUsers) * 100).toFixed(0) : 0;
    const revenueTrend = calcTrend(revenueThisMonth, revenueLastMonth);
    const orderTrend = calcTrend(ordersThisMonth, ordersLastMonth);

    // Monthly chart data (last 6 months)
    const months = getLastNMonths(6);
    const monthlyUserData = buildMonthlyDataFromUsers(allUsers, months);
    const monthlyPaymentData = buildMonthlyDataFromPayments(allPayments, months);
    const monthlyChartData = mergeMonthlyData(monthlyUserData, monthlyPaymentData);

    // User status distribution for pie chart
    const userStatusData = [
      { name: 'Active', value: activeUsers, color: '#10b981' },
      { name: 'Trial', value: trialUsers, color: '#6366f1' },
      { name: 'Expired', value: expiredUsers, color: '#f59e0b' },
    ].filter(d => d.value > 0);

    const otherCount = totalUsers - activeUsers - trialUsers - expiredUsers;
    if (otherCount > 0) {
      userStatusData.push({ name: 'Other', value: otherCount, color: '#94a3b8' });
    }

    // Recent users (sorted by newest)
    const recentUsers = [...allUsers]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 6);

    return {
      totalUsers, activeUsers, trialUsers, expiredUsers,
      newUsersThisMonth, newUsersLastMonth,
      totalRevenue, totalOrders,
      revenueThisMonth, revenueLastMonth,
      ordersThisMonth, ordersLastMonth,
      userTrend, activeRate, revenueTrend, orderTrend,
      monthlyChartData, userStatusData, recentUsers
    };
  }, [allUsers, allPayments]);

  // ─── LOADING STATE ──────────────────────────────
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#f1f5f9'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '16px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
            animation: 'pulse 1.5s ease-in-out infinite',
            boxShadow: '0 8px 30px rgba(99,102,241,0.3)'
          }}>
            <BarChart3 size={28} color="#fff" />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
            Loading Dashboard
          </div>
          <div style={{ fontSize: '14px', color: '#94a3b8' }}>Fetching analytics...</div>
          <style>{`@keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}`}</style>
        </div>
      </div>
    );
  }

  // ─── ERROR STATE ────────────────────────────────
  if (error && allUsers.length === 0) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#f1f5f9'
      }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <WifiOff size={48} style={{ color: '#ef4444', marginBottom: 16 }} />
          <h2 style={{ color: '#0f172a', marginBottom: 8 }}>Connection Error</h2>
          <p style={{ color: '#64748b', marginBottom: 20 }}>{error}</p>
          <button
            onClick={() => fetchDashboardData()}
            style={{
              padding: '12px 28px', background: '#6366f1', color: '#fff',
              border: 'none', borderRadius: '12px', fontSize: '14px',
              fontWeight: 700, cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ─── MAIN RENDER ────────────────────────────────
  return (
    <div style={{
      padding: '28px 32px', minHeight: '100vh',
      background: '#f1f5f9',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      {/* ════════ HERO BANNER ════════ */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        borderRadius: '20px', padding: '32px 36px',
        marginBottom: '28px', position: 'relative', overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(15,23,42,0.25)'
      }}>
        <div style={{
          position: 'absolute', top: -40, right: -40,
          width: 200, height: 200, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.15), transparent 70%)'
        }} />
        <div style={{
          position: 'absolute', bottom: -60, left: '40%',
          width: 300, height: 300, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.1), transparent 70%)'
        }} />

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', position: 'relative', flexWrap: 'wrap', gap: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: '14px',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(99,102,241,0.4)'
            }}>
              <BarChart3 size={24} color="#fff" />
            </div>
            <div>
              <h1 style={{
                margin: 0, fontSize: '26px', fontWeight: 800,
                color: '#fff', letterSpacing: '-0.5px'
              }}>Dashboard Overview</h1>
              <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8', fontWeight: 500 }}>
                {computed.totalUsers} users • Rs{computed.totalRevenue.toLocaleString()} total revenue
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => fetchDashboardData(true)}
              disabled={refreshing}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 20px',
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '12px', color: '#e2e8f0',
                fontSize: '13px', fontWeight: 600,
                cursor: refreshing ? 'wait' : 'pointer',
                transition: 'all 0.3s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            >
              <RefreshCw size={16} style={{
                animation: refreshing ? 'spin 1s linear infinite' : 'none'
              }} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 16px',
              background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.25)',
              borderRadius: '12px', color: '#a5b4fc',
              fontSize: '13px', fontWeight: 600
            }}>
              <Calendar size={14} />
              {new Date().toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric'
              })}
            </div>
          </div>
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      </div>

      {/* ════════ PRIMARY STAT CARDS ════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
        gap: '18px', marginBottom: '28px'
      }}>
        <StatCard
          title="Total Users"
          value={computed.totalUsers}
          icon={<Users size={22} />}
          gradient="linear-gradient(135deg, #3b82f6, #2563eb)"
          trend={computed.userTrend.value}
          trendUp={computed.userTrend.up}
          subtitle={`${computed.newUsersThisMonth} new this month`}
          delay={0}
          onClick={() => navigate('/admin/users')}
        />
        <StatCard
          title="Active Users"
          value={computed.activeUsers}
          icon={<CheckCircle size={22} />}
          gradient="linear-gradient(135deg, #10b981, #059669)"
          trend={`${computed.activeRate}% active`}
          trendUp={Number(computed.activeRate) > 50}
          subtitle={`${computed.trialUsers} on trial`}
          delay={100}
        />
        <StatCard
          title="Total Payments"
          value={computed.totalOrders}
          icon={<ShoppingCart size={22} />}
          gradient="linear-gradient(135deg, #f59e0b, #d97706)"
          trend={computed.orderTrend.value}
          trendUp={computed.orderTrend.up}
          subtitle={`${computed.ordersThisMonth} this month`}
          delay={200}
          onClick={() => navigate('/admin/payments')}
        />
        <StatCard
          title="Total Revenue"
          value={`Rs${computed.totalRevenue.toLocaleString()}`}
          icon={<DollarSign size={22} />}
          gradient="linear-gradient(135deg, #8b5cf6, #7c3aed)"
          trend={computed.revenueTrend.value}
          trendUp={computed.revenueTrend.up}
          subtitle={`Rs${computed.revenueThisMonth.toLocaleString()} this month`}
          delay={300}
        />
      </div>

      {/* ════════ SUBSCRIPTION STATS ════════ */}
      {subscriptionStats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
          gap: '18px', marginBottom: '28px'
        }}>
          <StatCard
            title="Active Subscriptions"
            value={subscriptionStats.activeSubscriptions || 0}
            icon={<Crown size={22} />}
            gradient="linear-gradient(135deg, #059669, #047857)"
            subtitle="Currently active plans"
            delay={400}
            onClick={() => navigate('/admin/subscriptions')}
          />
          <StatCard
            title="Expiring Soon"
            value={subscriptionStats.expiringSoon || 0}
            icon={<AlertTriangle size={22} />}
            gradient="linear-gradient(135deg, #f97316, #ea580c)"
            subtitle="Within next 7 days"
            trendUp={false}
            trend={(subscriptionStats.expiringSoon || 0) > 0 ? 'Action needed' : ''}
            delay={500}
          />
          <StatCard
            title="Monthly Sub Revenue"
            value={`Rs${(subscriptionStats.revenueThisMonth || 0).toLocaleString()}`}
            icon={<CreditCard size={22} />}
            gradient="linear-gradient(135deg, #6366f1, #4f46e5)"
            subtitle="Subscription revenue this month"
            delay={600}
          />
          {subscriptionStats.totalSubscriptions !== undefined && (
            <StatCard
              title="Total Subscriptions"
              value={subscriptionStats.totalSubscriptions || 0}
              icon={<Database size={22} />}
              gradient="linear-gradient(135deg, #ec4899, #db2777)"
              subtitle="All time subscriptions"
              delay={700}
              onClick={() => navigate('/admin/subscriptions')}
            />
          )}
        </div>
      )}

      {/* ════════ CHARTS ROW 1 ════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
        gap: '20px', marginBottom: '28px'
      }}>
        {/* Revenue Area Chart */}
        <div style={{
          background: '#fff', borderRadius: '18px', padding: '26px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
        }}>
          <SectionHeader
            icon={<TrendingUp size={18} />}
            title="Revenue Trend"
            subtitle="Last 6 months"
          />
          {computed.monthlyChartData.some(d => d.revenue > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={computed.monthlyChartData}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false}
                  tickFormatter={v => `Rs${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="revenue" name="Revenue"
                  stroke="#6366f1" strokeWidth={3} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={<TrendingUp size={40} />} message="No revenue data yet" />
          )}
        </div>

        {/* User Growth Line Chart */}
        <div style={{
          background: '#fff', borderRadius: '18px', padding: '26px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
        }}>
          <SectionHeader
            icon={<Activity size={18} />}
            title="User Registrations"
            subtitle="Last 6 months"
          />
          {computed.monthlyChartData.some(d => d.users > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={computed.monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="users" name="New Users"
                  stroke="#3b82f6" strokeWidth={3}
                  dot={{ fill: '#3b82f6', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, stroke: '#3b82f6', strokeWidth: 2, fill: '#fff' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={<Users size={40} />} message="No user data yet" />
          )}
        </div>
      </div>

      {/* ════════ CHARTS ROW 2 ════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
        gap: '20px', marginBottom: '28px'
      }}>
        {/* User Distribution Donut */}
        <div style={{
          background: '#fff', borderRadius: '18px', padding: '26px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
        }}>
          <SectionHeader
            icon={<Users size={18} />}
            title="User Distribution"
            subtitle="By current status"
          />
          {computed.userStatusData.length > 0 ? (
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 40, flexWrap: 'wrap'
            }}>
              <ResponsiveContainer width={220} height={220}>
                <PieChart>
                  <Pie data={computed.userStatusData} cx="50%" cy="50%"
                    innerRadius={60} outerRadius={95} paddingAngle={4}
                    dataKey="value" strokeWidth={0}>
                    {computed.userStatusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {computed.userStatusData.map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 16px',
                    background: `${item.color}08`, borderRadius: '10px',
                    border: `1px solid ${item.color}15`, minWidth: 160
                  }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: item.color, flexShrink: 0,
                      boxShadow: `0 0 8px ${item.color}40`
                    }} />
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                        {item.name}
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                        {item.value}
                        <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, marginLeft: 4 }}>
                          ({computed.totalUsers > 0
                            ? ((item.value / computed.totalUsers) * 100).toFixed(0)
                            : 0}%)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState icon={<Users size={40} />} message="No users to display" />
          )}
        </div>

        {/* Monthly Comparison Bar Chart */}
        <div style={{
          background: '#fff', borderRadius: '18px', padding: '26px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
        }}>
          <SectionHeader
            icon={<BarChart3 size={18} />}
            title="Monthly Overview"
            subtitle="Users vs Payments"
          />
          {computed.monthlyChartData.some(d => d.users > 0 || d.orders > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={computed.monthlyChartData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="users" name="New Users" fill="#6366f1" radius={[8,8,0,0]} maxBarSize={28} />
                <Bar dataKey="orders" name="Payments" fill="#10b981" radius={[8,8,0,0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={<BarChart3 size={40} />} message="No data to compare yet" />
          )}
        </div>
      </div>

      {/* ════════ RECENT USERS + QUICK ACTIONS ════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
        gap: '20px'
      }}>
        {/* Recent Users */}
        <div style={{
          background: '#fff', borderRadius: '18px', padding: '26px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
        }}>
          <SectionHeader
            icon={<UserPlus size={18} />}
            title="Recent Users"
            subtitle={`${computed.newUsersThisMonth} joined this month`}
            action={
              <button onClick={() => navigate('/admin/users')} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px',
                background: 'rgba(99,102,241,0.08)',
                border: '1px solid rgba(99,102,241,0.15)',
                borderRadius: '10px', color: '#6366f1',
                fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
              >
                <Eye size={14} /> View All
              </button>
            }
          />
          {computed.recentUsers.length === 0 ? (
            <EmptyState icon={<Users size={40} />} message="No users yet" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {computed.recentUsers.map((user, index) => {
                const statusColors = {
                  active:  { bg: '#dcfce7', text: '#16a34a', dot: '#22c55e' },
                  trial:   { bg: '#e0e7ff', text: '#4f46e5', dot: '#6366f1' },
                  expired: { bg: '#fef3c7', text: '#d97706', dot: '#f59e0b' },
                  blocked: { bg: '#fee2e2', text: '#dc2626', dot: '#ef4444' },
                };
                const sc = statusColors[user.status] || statusColors.active;
                const avatarColors = [
                  '#6366f1','#3b82f6','#10b981','#f59e0b','#ec4899','#8b5cf6'
                ];

                return (
                  <div key={user._id} style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '14px 16px', background: '#f8fafc',
                    borderRadius: '12px', transition: 'all 0.3s',
                    cursor: 'pointer', border: '1px solid transparent',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '12px',
                      background: `linear-gradient(135deg, ${
                        avatarColors[index % avatarColors.length]
                      }, ${avatarColors[(index + 1) % avatarColors.length]})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 700, fontSize: '16px', flexShrink: 0,
                      boxShadow: `0 4px 12px ${avatarColors[index % avatarColors.length]}30`
                    }}>
                      {user.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 700, fontSize: '14px', color: '#0f172a',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>{user.name}</div>
                      <div style={{
                        fontSize: '12px', color: '#94a3b8',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>{user.pharmacyName || user.email || user.phone}</div>
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', background: sc.bg,
                      borderRadius: '20px', flexShrink: 0
                    }}>
                      <CircleDot size={10} color={sc.dot} />
                      <span style={{
                        fontSize: '11px', fontWeight: 700, color: sc.text,
                        textTransform: 'capitalize'
                      }}>{user.status || 'active'}</span>
                    </div>
                    <div style={{
                      fontSize: '11px', color: '#94a3b8', flexShrink: 0, fontWeight: 500
                    }}>
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric'
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{
          background: '#fff', borderRadius: '18px', padding: '26px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
        }}>
          <SectionHeader
            icon={<Zap size={18} />}
            title="Quick Actions"
            subtitle="Navigate to key areas"
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <QuickActionButton
              icon={<Users size={20} />}
              label="Manage Users"
              description={`${computed.totalUsers} total users`}
              gradient="linear-gradient(135deg, #3b82f6, #2563eb)"
              onClick={() => navigate('/admin/users')}
            />
            <QuickActionButton
              icon={<Building2 size={20} />}
              label="User Parties"
              description="Manage business parties"
              gradient="linear-gradient(135deg, #8b5cf6, #7c3aed)"
              onClick={() => navigate('/admin/user-parties')}
            />
            <QuickActionButton
              icon={<FileText size={20} />}
              label="User Transactions"
              description="View transaction history"
              gradient="linear-gradient(135deg, #06b6d4, #0891b2)"
              onClick={() => navigate('/admin/user-transactions')}
            />
            <QuickActionButton
              icon={<Crown size={20} />}
              label="Subscription Plans"
              description="Create & manage plans"
              gradient="linear-gradient(135deg, #f59e0b, #d97706)"
              onClick={() => navigate('/admin/subscription-plans')}
            />
            <QuickActionButton
              icon={<Shield size={20} />}
              label="Subscriptions"
              description={subscriptionStats
                ? `${subscriptionStats.activeSubscriptions || 0} active`
                : 'Monitor subscriptions'}
              gradient="linear-gradient(135deg, #10b981, #059669)"
              onClick={() => navigate('/admin/subscriptions')}
            />
            <QuickActionButton
              icon={<CreditCard size={20} />}
              label="Payments"
              description={`${computed.totalOrders} total payments`}
              gradient="linear-gradient(135deg, #ec4899, #db2777)"
              onClick={() => navigate('/admin/payments')}
            />
            <QuickActionButton
              icon={<Settings size={20} />}
              label="System Settings"
              description="Configure preferences"
              gradient="linear-gradient(135deg, #64748b, #475569)"
              onClick={() => navigate('/settings')}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        textAlign: 'center', padding: '24px 0 8px',
        color: '#94a3b8', fontSize: '12px', fontWeight: 500
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
        }}>
          <Sparkles size={14} />
          {lastUpdated
            ? `Last updated ${lastUpdated.toLocaleTimeString()}`
            : 'Dashboard loaded'}
          {' • '}
          {computed.totalUsers} users
          {' • '}
          {computed.totalOrders} payments
        </div>
      </div>
    </div>
  );
}